import { Icon, IconButton, Stack, Typography } from "@mui/material";
import Page from "common/components/Page";
import React, { useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import AwsTarget from "pages/Targets/components/AwsTarget";
import AiIcon from "assets/icons/magic.svg";
import ActionButton from "common/components/ActionButton";
import ClickableTypography from "common/components/ClickableTypography";
import CodeEditor from "common/components/CodeEditor";
import Fab from "common/components/Fab";
import { MiniLoader } from "common/components/Loader";
import MultiLevelMenu from "common/components/MultilevelMenu";
import SidePanel from "common/components/SidePanel";
import useConfirm from "common/hooks/useConfirm";
import useFetcher from "common/hooks/useFetcher";
import copyContent from "common/utils/copyContent";
import { TypeMap } from "constants";
import validate from "constants/validate";
import CommandCopy from "pages/Action/components/Copy";
import CommandRestore from "pages/Action/components/Restore";
import { VersionInfo } from "pages/Package";
import FilesModal from "pages/SystemDash/component/Files";
import JobsModal from "pages/SystemDash/component/Jobs";
import toast from "react-hot-toast";
import { PackageIcon } from "routes";
import ConfigDrawer from "./components/ConfigDrawer";
import CommandDebugPanel from "./components/Execute";
import TestPackage from "./components/TestPackage";

const customSnippets = [
  {
    caption: "e1Dataset",
    snippet: "context.dataset",
    type: "snippet",
    meta: "current dataset"
  },
  {
    caption: "e1HandlerSelection",
    snippet:
      "for resource in context.dataset: \n\tif not context.is_selected(resource):\n\t\tcontinue\n\t${1:# code to run on selected resources goes here}",
    type: "snippet",
    meta:
      "logic to write logic against only selected resources from the dataset"
  },
  {
    caption: "e1Timestamp",
    snippet: "e1.timestamp()",
    type: "snippet",
    meta:
      "get a date string of the current time in this format, %d-%b-%Y (%H:%M:%S.%f)"
  },
  {
    caption: "e1FileUpload",
    snippet: "e1.file.upload(${1:LOCAL_FILE_PATH})",
    type: "snippet",
    meta: "upload file to system"
  },
  {
    caption: "e1FileDownload",
    snippet: "e1.file.download(${1:FILE_NAME},${2:LOCAL_FILE_PATH})",
    type: "snippet",
    meta: "download file from system"
  },
  {
    caption: "e1FileDelete",
    snippet: "e1.file.delete(${1:FILE_NAME})",
    type: "snippet",
    meta: "delete file from system"
  },
  {
    caption: "e1FileExists",
    snippet: "e1.file.exists(${1:FILE_NAME})",
    type: "snippet",
    meta: "check if files exists"
  },
  {
    caption: "e1TenantId",
    snippet: "e1.tenant().id",
    type: "snippet",
    meta: "access current tenant id"
  },
  {
    caption: "e1TenantAlias",
    snippet: "e1.tenant().alias",
    type: "snippet",
    meta: "access current tenant alias"
  },
  {
    caption: "e1TenantGeo",
    snippet: "e1.tenant().geo",
    type: "snippet",
    meta: "access current tenant geo"
  },
  {
    caption: "e1PlatformSession",
    snippet: "config = e1.platform.session()",
    type: "snippet",
    meta: "get platform config for current session"
  },
  {
    caption: "e1PlatformAssume",
    snippet: "config = e1.platform.assume(${1:TENANT},${2:GEO})",
    type: "snippet",
    meta: "get platform config for a given tenant id and geo"
  },
  {
    caption: "e1TenantAdd",
    snippet:
      'tenant_id = e1.tenant.add({\n\t"type": "${1:PLATFORM_NAME}",\n\t"config": {\n\t\t"id": "${2:TENANT_ID}",\n\t\t"alias": "${3:TENANT_ALIAS}",\n\t},\n\t"tags": ["${5:TAG}"],\n\t"acl": []\n})',
    type: "snippet",
    meta: "add or overwrite a tenant"
  },
  {
    caption: "e1InputDefaultUpdate",
    snippet:
      'e1.inputs.update_default({"command": None, "name": ${1:INPUT_NAME} , "string":${2:VALUE}})',
    type: "snippet",
    meta: "update an input's default value"
  },
  // {
  //   caption: "e1CommandCreateSchedule",
  //   snippet:
  //     'e1.command.schedule.create(\n{\n  "command":"${1:COMMAND_ID}", # (REQUIRED) command id\n  "inputs":{"${2:key}": "${3:value}"}, # (REQUIRED) dictionary for command inputs\n  "name":"${4:SCHEDULE_NAME}", # (REQUIRED) name for the schedule\n  "target":"${5:TARGET_ID}", # (OPTIONAL) target id for command to run against\n  "cron": "${6:cron_expression}", # (REQUIRED) aws cron expression e.g. 47 18 * * ? *\n  "once": ${7:False}, # (OPTIONAL) whether to execute only once\n  "selection": { # (OPTIONAL) defines resources to act on\n    "selected":${8:[]}, # (OPTIONAL) list of resource unique ids\n    "unselected":${9:[]}, # (OPTIONAL) list to exempt when selectAll is True\n    "selectAll": ${10:False}, # (OPTIONAL) whether all resources should be selected\n    "query": ${11:[]} # (OPTIONAL) list of filter dictionaries\n  }\n})',
  //   type: "snippet",
  //   meta: "Create a scheduled command execution",
  // },
  // {
  //   caption: "e1CommandDeleteSchedule",
  //   snippet: 'e1.command.schedule.delete(name="${1:SCHEDULE_NAME}")',
  //   type: "snippet",
  //   meta: "Delete a scheduled command execution",
  // },
  {
    caption: "e1FileDownloadSheet",
    snippet:
      'e1.file.download("${1:FILE_NAME}.csv", "/tmp/${1:FILE_NAME}.csv")\nchecks = []\nwith open("/tmp/${1:FILE_NAME}.csv", "r") as csvfile:\n    reader = csv.DictReader(csvfile)\n    data = [row for row in reader]',
    type: "snippet",
    meta: "Download a csv file and parse its contents"
  }
];

const Panel = ({
  menu,
  system,
  onClose,
  data,
  setData,
  dataBacked,
  setE1Pkg
}) => {
  return (
    <Stack>
      <ConfigDrawer
        {...{ menu, data, system, setData, dataBacked, setE1Pkg }}
        close={onClose}
      />
    </Stack>
  );
};
const PackageInfo = ({ e1Pkg }) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const fetcher = useFetcher();

  const fetchPkg = async () => {
    setLoading(true);
    let [pkg, version] = e1Pkg.split("==");
    await fetcher.get(
      "fetching package info",
      `/api/pkg/${pkg}/version/${version}`,
      ({ pkg }) => {
        setData(pkg);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );
    setLoading(false);
  };
  useEffect(() => {
    if (!e1Pkg) return;
    fetchPkg();
  }, [e1Pkg]);
  if (loading)
    return (
      <div
        style={{
          marginTop: "100px",
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <MiniLoader />
        <Typography sx={{ marginTop: "60px" }}>loading package info</Typography>
      </div>
    );
  let docs = data?.doc || "";
  let packages = data?.packages || [];
  return (
    <Stack p={4}>
      <VersionInfo docs={docs} packages={packages} />
    </Stack>
  );
};
export default function Action({ role }) {
  const [loading, setLoading] = React.useState(false);
  // const [generator, setGenerator] = React.useState(false);
  // const [reviewer, setReviewer] = React.useState(false);
  const [execute, setExecute] = React.useState("");
  const [copy, setCopy] = React.useState(false);
  const [restore, setRestore] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [err, setError] = React.useState(false);
  const [properties, setProperties] = React.useState([]);
  const [systemData, setSystemData] = React.useState({});
  const [newCommand, setNewCommand] = React.useState(false);
  const { id, action } = useParams();
  const [jobsOpen, setJobsOpen] = React.useState(false);
  let [searchParams, setSearchParams] = useSearchParams();
  const systemname = searchParams.get("system") || "";
  const pkg = searchParams.get("pkg") || "";
  const isNew = action === "new";
  const [refreshKey, setRefreshKey] = React.useState("1");
  const [configs, setConfigs] = React.useState(false);
  const [filesOpen, setFilesOpen] = React.useState(false);
  const [dataBacked, setDataBacked] = React.useState(false);
  const [e1Pkg, setE1Pkg] = React.useState(false);
  const [eTag, setETag] = React.useState("");

  const [code, setCodeState] = React.useState(
    "import e1\n\ndef handler(context):\n    return None\n"
  );
  const [originalCode, setOriginalCode] = React.useState(
    "import e1\n\ndef handler(context):\n    return None\n"
  );
  const [testPackage, setTestPackage] = React.useState(false);
  const [data, setDataState] = React.useState({
    name: "",
    description: "",
    inputs: [],
    envs: [],
    packages: [],
    tags: [],
    acl: [],
    // scan: [],
    targeting: {
      enabled: false,
      select: false,
      target: null
    }
  });
  const setCode = d => {
    setCodeState(d);
    setRefreshKey(Math.random());
    if (!dirty) setDirty(true);
  };
  const setData = d => {
    setDataState(d);
    if (!dirty) setDirty(true);
  };

  const fetcher = useFetcher();

  const navigate = useNavigate();

  const saveCommand = async (overwrite = false) => {
    const payload = {
      ...data,
      code,
      eTag: overwrite ? 1 : eTag
    };
    if (!validate("command_edit", payload)) return;
    await fetcher.post(
      "saving action",
      `/api/sys/${id}/action/${action}`,
      payload,
      data => {
        if (data?.error) {
          if (data?.confirmOverwrite) {
            // model that asks to continue anyway
            return openConfirmOw(true);
          } else {
            return toast.error(data?.error);
          }
        }
        setDirty(false);
        if (action === "new" && data?.id)
          return navigate(
            `/app/sys/${id}/action/${data?.id}?system=${systemname}`
          );
        setRefreshKey(Math.random());
        fetchCommand();
      },
      () => {}
    );
  };
  const deleteCommand = async () => {
    setLoading(true);
    await fetcher.delete(
      "deleting command",
      `/api/sys/${id}/action/${action}`,
      ({ error }) => {
        if (error) return toast.error("error deleting command");
        navigate(`/app/sys/${id}/dash`);
      },
      () => {
        setLoading(false);
        setError(true);
      }
    );
    setLoading(false);
  };
  const fetchCommandBase = async (system_id, action) => {
    try {
      setDirty(false);
      await fetcher.get(
        "fetching command",
        `/api/sys/${system_id}/action/${action}`,
        ({ action, system, eTag }) => {
          setETag(eTag);
          setSystemData(system);
          setDataBacked(action?.data_backed);
          setProperties(action?.properties || []);
          if (isNew) return setError(false);
          delete action?.data_backed;
          delete action?.properties;
          setDataState(action);
          setCodeState(action.code);
          setOriginalCode(action.code);
          setError(false);
        },
        () => {
          setError(true);
        }
      );
    } catch {}
  };
  const fetchCommand = async (override, systemOverride = null) => {
    setLoading(true);
    setDirty(false);
    const system_id = systemOverride || id;
    await fetchCommandBase(system_id, override || action);
    // await fetcher.get(
    //   "fetching command",
    //   `/api/sys/${system_id}/action/${override || action}`,
    //   ({ action, system }) => {
    //     setSystemData(system);
    //     setDataBacked(action?.data_backed);
    //     setProperties(action?.properties || []);
    //     if (isNew) return setError(false);
    //     delete action?.data_backed;
    //     delete action?.properties;
    //     setDataState(action);
    //     setCodeState(action.code);
    //     setOriginalCode(action.code);
    //     setError(false);
    //   },
    //   () => {
    //     setLoading(false);
    //     setError(true);
    //   }
    // );
    setLoading(false);
  };
  const [openDeleteModal, DeleteModal] = useConfirm({
    title: `Delete Command`,
    message: `Are you sure you want to delete this command? This action can't be undone.`,
    callback: deleteCommand
  });
  const [openConfirmOw, ConfirmOwModal] = useConfirm({
    title: `Save Command anyway?`,
    message: `External modifications have been made to this command's configuration. Are you sure you want to save this command configuration? This action can't be undone.`,
    callback: () => {
      saveCommand(true);
    },
    onCancel: () => fetchCommand()
  });

  React.useEffect(() => {
    fetchCommand();
  }, [searchParams, action]);
  if (err) {
    return (
      <Page
        back={pkg ? `/app/packages/${pkg}?version=0` : `/app/sys/${id}/dash`}
        loading={loading}
        header={`Cannot fetch command (${id})`}
      />
    );
  }
  const PanelMenu = React.useCallback(() => {
    return (
      <div style={{ position: "relative", alignSelf: "flex-end" }}>
        <MultiLevelMenu
          button={props => (
            <ActionButton
              handler={props.onClick}
              circle
              dark
              noload
              icon="more_vert"
            />
          )}
          menu={{
            main: {
              "Copy Command Id": {
                leftIcon: <Icon>content_copy</Icon>,
                onClick: () => {
                  copyContent(action);
                  toast.success("copied command id");
                }
              },
              "Copy Command": {
                leftIcon: <Icon>content_copy</Icon>,
                onClick: () => setCopy(true)
              },
              "Restore Command": {
                leftIcon: <Icon>settings_backup_restore</Icon>,
                onClick: () => setRestore(true)
              },
              "Test Package": {
                leftIcon: <PackageIcon size={28} fill="#ff914c" />,
                onClick: () => setTestPackage(true)
              },
              "Delete Command": {
                leftIcon: <Icon color="error">delete</Icon>,
                onClick: () => openDeleteModal(true)
              }
            }
          }}
        />
      </div>
    );
  }, [action]);
  return (
    <>
      <TestPackage
        {...{
          system: { id, ...systemData },
          command: data,
          edit: testPackage,
          onClose: () => setTestPackage(false)
        }}
      />
      {/* <SidePanel
        width={800}
        open={generator}
        closeDrawer={() => setGenerator(null)}
        polybg
        props={{
          system: id,
          action: action,
          setCode
        }}
      >
        {CodeGenerator}
      </SidePanel>*/}
      {/* <SidePanel
        width={800}
        open={reviewer}
        closeDrawer={() => setReviewer(null)}
        polybg
        props={{
          system: id,
          action: action,
          data,
          refresh: () => fetchCommandBase(id, action)
        }}
      >
        {CodeReview}
      </SidePanel>*/}
      <SidePanel
        width={600}
        open={!!execute}
        closeDrawer={() => {
          setExecute(null);
          // fetchCommandBase(id, action);
        }}
        props={{
          system: id,
          id: execute
        }}
      >
        {CommandDebugPanel}
      </SidePanel>
      <FilesModal
        role={role}
        // system={system?.id}
        open={filesOpen}
        setOpen={setFilesOpen}
      />
      <JobsModal system={id} open={jobsOpen} setOpen={setJobsOpen} />
      <Page
        refresh={!isNew ? () => fetchCommand(action) : null}
        back={pkg ? `/app/packages/${pkg}?version=0` : `/app/sys/${id}/dash`}
        header={
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              sx={{
                userSelect: "none",
                marginTop: "4px"
              }}
              variant="h4"
            >{`${systemname} /`}</Typography>
            <ClickableTypography
              onClick={v =>
                navigate(
                  `/app/sys/${id}/action/${
                    (systemData?.actions || []).find(o => o.name === v).id
                  }?system=${encodeURI(systemData.name)}`
                )
              }
              options={(systemData?.actions || [])
                .filter(a => a.id !== action)
                .map(a => a.name)}
              variant="h4"
            >
              {!isNew ? `${data?.name}` : "New Command"}
            </ClickableTypography>
          </Stack>
          // !isNew
          //   ? `${systemname} / ${data?.name} command`
          //   : `${systemname} / New Command`
        }
        dynamicSize
        loading={loading}
        actions={[
          ...[
            <ActionButton
              noload
              icon="rocket_launch"
              circle
              handler={() => setExecute(data.id)}
            />,
            <ActionButton
              noload
              icon="dashboard"
              circle
              handler={() => navigate(`/app/sys/${id}/dash`)}
            />,
            <ActionButton
              noload
              icon="sms"
              circle
              handler={() => setJobsOpen(true)}
            />,
            <ActionButton
              noload
              icon="attachment"
              circle
              handler={() => setFilesOpen(true)}
            />,
            <ActionButton
              noload
              icon="more_vert"
              circle
              handler={() => setConfigs("base")}
            />
          ]
        ]}
      >
        <div>
          <CodeEditor
            parent="app-body"
            name="code-editor"
            value={code}
            setValue={setCode}
            liveSnippets={[
              ...customSnippets,
              ...(data?.inputs || [])?.map(i => {
                return {
                  caption: `e1Input${i.name
                    .split("_")
                    .map(i => {
                      return i[0].toUpperCase() + i.substr(1);
                    })
                    .join("")}`,
                  snippet: `context.inputs.${i.name}`,
                  type: "snippet",
                  meta: "value of given input"
                };
              }),
              ...(data?.envs || [])?.reduce((a, i) => {
                return [
                  ...a,
                  {
                    caption: `e1EnvRead${i.name
                      .split("_")
                      .map(i => {
                        return i[0].toUpperCase() + i.substr(1);
                      })
                      .join("")}`,
                    snippet: `context.env.${i.name}`,
                    type: "snippet",
                    meta: "value of given variable"
                  },
                  {
                    caption: `e1EnvUpdate${i.name
                      .split("_")
                      .map(i => {
                        return i[0].toUpperCase() + i.substr(1);
                      })
                      .join("")}`,
                    snippet: `e1.env.update(context.refs.env["${i.name}"],"\${1:VALUE}")`,
                    type: "snippet",
                    meta: "update a given variable"
                  }
                ];
              }, [])
            ]}
            snippets={(editor, insert) => {
              const inputs = data?.inputs?.reduce(
                (a, { name }) => ({
                  ...a,
                  [name]: { onClick: () => insert(`context.inputs.${name}`) }
                }),
                {}
              );
              const packages = data?.packages
                ?.filter(({ source }) => source)
                ?.reduce(
                  (a, { pkg }) => ({
                    ...a,
                    [pkg]: { onClick: () => setE1Pkg(pkg) }
                  }),
                  {}
                );
              const env = data.envs.reduce(
                (a, { name, id }) => ({
                  ...a,
                  [`Use ${name}`]: {
                    onClick: () => insert(`context.env.${name}`)
                  },
                  [`Update ${name}`]: {
                    onClick: () =>
                      insert(`e1.env.update(context.refs.env["${name}"],value)`)
                  }
                }),
                {}
              );
              return {
                main: {
                  System: {
                    goToMenu: "system"
                  },
                  Files: {
                    goToMenu: "file"
                  },
                  Tenant: {
                    goToMenu: "tenant"
                  },
                  Inputs: {
                    goToMenu: "inputs"
                  },
                  Packages: {
                    goToMenu: "packages"
                  },
                  "Environment Variables": {
                    goToMenu: "env"
                  },
                  Convert: {
                    goToMenu: "convert"
                  }
                },
                system: {
                  "Is Selected": {
                    onClick: () => insert("context.is_selected(RESOURCE)")
                  },
                  "System Dataset": {
                    onClick: () => insert("context.dataset")
                  },
                  "System Object": {
                    onClick: () =>
                      insert(
                        JSON.stringify(
                          properties.reduce(
                            (o, p) =>
                              (o[p.name] = {
                                ...o,
                                [p.name]: TypeMap[p.type]?.default
                              }),
                            {}
                          ),
                          null,
                          4
                        )
                      )
                  }
                },
                file: {
                  "Upload File": {
                    onClick: () => insert("e1.file.upload(LOCAL_FILE_PATH)")
                  },
                  "Download File": {
                    onClick: () =>
                      insert("e1.file.download(FILE_NAME,LOCAL_FILE_PATH)")
                  },
                  "Delete File": {
                    onClick: () => insert("e1.file.delete(FILE_NAME)")
                  },
                  "Check File Exists": {
                    onClick: () => insert("e1.file.exists(FILE_NAME)")
                  }
                },
                tenant: {
                  "Tenant id": {
                    onClick: () => insert("e1.tenant().id")
                  },
                  "Tenant alias": {
                    onClick: () => insert("e1.tenant().alias")
                  },
                  "Tenant geo": {
                    onClick: () => insert("e1.tenant().geo")
                  },
                  "Tenant info": {
                    onClick: () => insert("e1.tenant()")
                  },
                  "(AWS) Assume into tenant": {
                    onClick: () => insert("e1.cloud.aws.assume(ACCOUNT,REGION)")
                  },
                  "(AWS) Current Session": {
                    onClick: () => insert("e1.cloud.aws.session()")
                  },
                  "(Azure) Assume into tenant": {
                    onClick: () =>
                      insert("e1.cloud.azure.assume(SUBSCRIPTION_ID,REGION)")
                  },
                  "(Azure) Current Session": {
                    onClick: () => insert("e1.cloud.azure.session()")
                  },
                  "(Azure) Subscription ID": {
                    onClick: () => insert('os.getenv("AZURE_SUBSCRIPTION_ID")')
                  }
                },
                env,
                inputs,
                packages,
                convert: {
                  "String to Datetime": {
                    onClick: () => insert("datetime.strptime(INPUT,'%Y-%m-%d')")
                  }
                }
              };
            }}
            onMenu={() => setConfigs(true)}
            activeTools={[
              <IconButton
                onClick={() =>
                  navigate(
                    `/app/ai/sys/builder?system=${id}&command=${action}&system_name=${searchParams.get(
                      "system"
                    )}`
                  )
                } //setGenerator(true)}
                className="cssbuttons-io-button"
                sx={{
                  alignSelf: "right",
                  padding: 0,
                  margin: 0,
                  height: "40px",
                  width: "40px",
                  backgroundColor: "#a827fa",
                  "&:hover": {
                    backgroundColor: "#bb1fe2"
                  }
                }}
              >
                <img height="24px" width="24px" src={AiIcon} />
              </IconButton>
            ]}
            passiveTools={(editor, insert) => [
              {
                icon: (
                  <IconButton
                    onClick={() =>
                      navigate(
                        `/app/ai/sys/builder?system=${id}&command=${action}&system_name=${searchParams.get(
                          "system"
                        )}`
                      )
                    } //setGenerator(true)}
                    className="cssbuttons-io-button"
                    sx={{
                      alignSelf: "right",
                      padding: 0,
                      margin: 0,
                      height: "40px",
                      width: "40px",
                      backgroundColor: "#a827fa",
                      "&:hover": {
                        backgroundColor: "#bb1fe2"
                      }
                    }}
                  >
                    <img height="24px" width="24px" src={AiIcon} />
                  </IconButton>
                )
              }
              // {
              //   icon: (
              //     <IconButton
              //       onClick={() => setReviewer(true)}
              //       sx={{
              //         alignSelf: "right",
              //         padding: 0,
              //         margin: 0,
              //         height: "40px",
              //         width: "40px",
              //         "&:hover": {
              //           backgroundColor: "#a827fa"
              //         }
              //       }}
              //     >
              //       <Icon
              //         sx={{
              //           height: "24px !imporant",
              //           width: "24px !important",
              //           color: "#fff !important"
              //         }}
              //       >
              //         rate_review
              //       </Icon>
              //       {/* <<img height="24px" width="24px" src={AiIcon} />> */}
              //     </IconButton>
              //   )
              // }
              // ...(data?.scan?.length
              // ...(true
              //   ? [
              //       {
              //         component: (
              //           <CodeReview
              //             data={data}
              //             action={action}
              //             system={id}
              //             refresh={fetchCommand}
              //           />
              //         ),
              //       },
              //     ]
              //   : []),
            ]}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))"
          }}
        >
          <CommandCopy
            {...{
              open: copy,
              refresh: (id, system) => fetchCommand(id, system),
              close: () => {
                setCopy(false);
              },
              data,
              code
            }}
          />
          <CommandRestore
            {...{
              open: restore,
              close: () => setRestore(false),
              action,
              refresh: () => fetchCommand()
            }}
          />
        </div>
        <Fab
          active={dirty}
          icon="save"
          onClick={() => {
            setConfigs(null);
            saveCommand();
          }}
        />
        <ConfirmOwModal />
        <DeleteModal />
        <SidePanel
          width={700}
          open={!!configs}
          closeDrawer={() => {
            if (configs !== "base") {
              window.codeEditorFocusHandler();
            }
            setConfigs(null);
          }}
          props={{
            menu: PanelMenu,
            system: id,
            setE1Pkg,
            data,
            setData,
            dataBacked
          }}
        >
          {Panel}
        </SidePanel>
        <SidePanel
          width={600}
          open={!!e1Pkg}
          closeDrawer={() => {
            setE1Pkg(null);
          }}
          props={{
            e1Pkg
          }}
        >
          {PackageInfo}
        </SidePanel>
      </Page>
    </>
  );
}
